import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";

const roundTours = [
  {
    slug: "multi-day-8",
    img: img("activities/day-trips/day-trips-tour6-01.jpg"),
    titleKey: "multiday_8_title",
    subtitleKey: "multiday_8_subtitle",
    descKey: "multiday_8_desc",
    tagKey: "multiday_8_tag",
    adultKey: "roundtrip_8_adult" as string | undefined,
    childKey: "roundtrip_8_child" as string | undefined,
  },
  {
    slug: "multi-day-9",
    img: img("activities/day-trips/day-trips-tour1-01.jpg"),
    titleKey: "multiday_9_title",
    subtitleKey: "multiday_9_subtitle",
    descKey: "multiday_9_desc",
    tagKey: "multiday_9_tag",
    adultKey: "roundtrip_9_adult" as string | undefined,
    childKey: "roundtrip_9_child" as string | undefined,
  },
  {
    slug: "multi-day-10",
    img: img("activities/multi-day-tours/multi-day-tours-tour10-01.jpg"),
    titleKey: "multiday_1_title",
    subtitleKey: "multiday_1_subtitle",
    descKey: "multiday_1_desc",
    tagKey: "multiday_1_tag",
    adultKey: "roundtrip_10_adult",
    childKey: "roundtrip_10_child",
  },
  {
    slug: "multi-day-11",
    img: img("activities/multi-day-tours/multi-day-tours-tour11-01.jpg"),
    titleKey: "multiday_2_title",
    subtitleKey: "multiday_2_subtitle",
    descKey: "multiday_2_desc",
    tagKey: "multiday_2_tag",
    adultKey: "roundtrip_11_adult",
    childKey: "roundtrip_11_child",
  },
  {
    slug: "multi-day-12",
    img: img("activities/multi-day-tours/multidaytours-tour12-01.jpg"),
    titleKey: "multiday_3_title",
    subtitleKey: "multiday_3_subtitle",
    descKey: "multiday_3_desc",
    tagKey: "multiday_3_tag",
    adultKey: "roundtrip_12_adult",
    childKey: "roundtrip_12_child",
  },
];

export default function MultiDayToursPage() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const { priceMap, money } = usePriceMap();

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("multiday_eyebrow")}
          title={t("multiday_title")}
          subtitle={t("multiday_intro")}
          image={img("activities/multi-day-tours/header-multidaytours-01.jpg")}
          imageAlt="Bali multi-day tours"
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-8">
              <p className="text-foreground-700 leading-relaxed">
                {t("multiday_desc")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {roundTours.map((rt, i) => (
                <div
                  key={i}
                  className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-600 transition-all"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={rt.img}
                      alt={t(rt.titleKey)}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 right-4 bg-background-50/95 text-foreground-900 text-xs font-semibold px-3 py-1 rounded-full">
                      {t(rt.tagKey)}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-1">
                      {t(rt.titleKey)}
                    </h3>
                    <p className="text-sm font-medium text-accent-700 mb-3">
                      {t(rt.subtitleKey)}
                    </p>
                    <div className="w-10 h-0.5 bg-accent-500 mb-4"></div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {t(rt.descKey)}
                    </p>
                    <div className="mt-4 pt-4 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap sm:order-2">
                        {rt.adultKey && priceMap[rt.adultKey] != null ? (
                          isDe
                            ? `${money(priceMap[rt.adultKey])} / Erw.${rt.childKey && priceMap[rt.childKey] != null ? ` · ${money(priceMap[rt.childKey])} / Kind` : ""}`
                            : `${money(priceMap[rt.adultKey])} / adult${rt.childKey && priceMap[rt.childKey] != null ? ` · ${money(priceMap[rt.childKey])} / child` : ""}`
                        ) : (
                          t("tour_detail_price_on_request")
                        )}
                      </span>
                      <LocalizedLink
                        to={`/tour/${rt.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors sm:order-1"
                      >
                        {t("tour_detail_read_more")}
                        <i className="ri-arrow-right-line"></i>
                      </LocalizedLink>
                    </div>
                  </div>
                </div>
              ))}

              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                  {t("multiday_heading_1")}
                </h3>
                <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                <p className="text-foreground-700 leading-relaxed mb-6">
                  {t("multiday_p1")}
                </p>
                <p className="text-foreground-700 leading-relaxed mb-6">
                  {t("multiday_p2")}
                </p>
                <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                <p className="text-foreground-700 leading-relaxed">
                  {t("multiday_p3")}
                </p>
              </div>
            </div>

            <div className="mt-10 flex flex-col items-center">
              <LocalizedLink
                to="/booking-multiday"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-calendar-check-line"></i>
                {t("multiday_book_tour")}
              </LocalizedLink>
              <div className="max-w-2xl">
                <BookingCompanyNotice isDe={isDe} />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {t("multiday_cta_title")}
                </h2>
                <p className="mt-2 text-foreground-700 text-sm">
                  {t("multiday_cta_sub")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <LocalizedLink
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-7 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-mail-send-line"></i>
                  {t("multiday_cta_contact")}
                </LocalizedLink>
                <a
                  href="https://wa.me/6281246218000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#1DA851] hover:bg-[#15823F] text-white px-7 py-3.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}